import { Component, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { matchFieldsValidator } from 'src/app/validators/match-field.validators';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ApiError } from 'src/app/trip-calculator/models/api-response';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { firstValueFrom } from 'rxjs';
import { PasswordModule } from 'primeng/password';
import { DividerModule } from 'primeng/divider';
import { passwordStrengthValidator } from 'src/app/validators/password-strength.validators';
import { environment } from 'src/environments/environment';
import { LucideAngularModule, Lock } from 'lucide-angular';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  standalone: true,
  imports: [
    ButtonModule,
    ReactiveFormsModule,
    InputTextModule,
    TranslocoModule,
    CommonModule,
    IconFieldModule,
    InputIconModule,
    PasswordModule,
    DividerModule,
    LucideAngularModule,
  ],
})
export class ChangePasswordComponent {
  readonly lockIcon = Lock;
  passwordForm: FormGroup;
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  isCurrentPasswordVisible = false;
  isNewPasswordVisible = false;
  isConfirmPasswordVisible = false;
  translocoService = inject(TranslocoService);
  constructor() {
    this.passwordForm = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, passwordStrengthValidator()]],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: matchFieldsValidator('newPassword', 'confirmPassword'),
      },
    );
  }

  onSubmit() {
    if (this.passwordForm.valid) {
      // Traitement de la soumission ici
      const currentPassword = this.passwordForm.value.currentPassword;
      const newPassword = this.passwordForm.value.newPassword;
      this.http
        .post(`${environment.apiUrl}/auth/change-password`, {
          currentPassword,
          newPassword,
        })
        .subscribe({
          next: async () => {
            const message = await firstValueFrom(
              this.translocoService.selectTranslate(
                'accountMenu.password.updatePasswordSuccess',
              ),
            );
            this.notificationService.success(message);
            this.passwordForm.reset();
          },
          error: async (error: ApiError) => {
            if (error.error.code === 'E_CURRENT_PASSWORD_INCORRECT') {
              const message = await firstValueFrom(
                this.translocoService.selectTranslate(
                  'accountMenu.password.updatePasswordErrorIncorrectCurrent',
                ),
              );
              this.notificationService.error(message);
            } else {
              const message = await firstValueFrom(
                this.translocoService.selectTranslate(
                  'accountMenu.password.updatePasswordError',
                ),
              );
              this.notificationService.error(message);
            }
          },
        });
    } else {
      this.passwordForm.markAllAsTouched();
    }
  }
  get notMatchingError() {
    return this.passwordForm.hasError('notMatching');
  }
  get passwordStrengthError() {
    return this.passwordForm.get('newPassword')?.hasError('passwordStrength');
  }
  isInvalid(controlName: string): boolean {
    const control = this.passwordForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
  getControlErrors(controlName: string): string[] {
    const control = this.passwordForm.get(controlName);
    return control && control.errors ? Object.keys(control.errors) : [];
  }
}
